import { Configuration, PopupRequest, PublicClientApplication } from '@azure/msal-browser';
import { getRuntimeConfig } from './runtimeConfig';

// --- Lazy-initialized singletons (populated by initAuth) ---

let _msalInstance: PublicClientApplication | null = null;
let _loginRequest: PopupRequest | null = null;
let _apiTokenRequest: { scopes: string[]; forceRefresh: boolean } | null = null;
let _expectedTenantId: string | null = null;
let _apiScope: string | null = null;

/**
 * Initialize MSAL from runtime config.
 * Must be called once (from index.tsx) before rendering <MsalProvider>.
 *
 * Two modes:
 * - If azure_api_scope is set: requests access tokens with custom scope
 * - If null: SPA public client mode, uses ID tokens (openid + profile scopes)
 */
export async function initAuth(): Promise<PublicClientApplication> {
  const cfg = getRuntimeConfig();

  const msalConfig: Configuration = {
    auth: {
      clientId: cfg.azure_client_id,
      authority: `https://login.microsoftonline.com/${cfg.azure_tenant_id}`,
      redirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      windowHashTimeout: 60000,
      iframeHashTimeout: 6000,
      loadFrameTimeout: 0,
    },
  };

  _apiScope = cfg.azure_api_scope;
  _expectedTenantId = cfg.azure_tenant_id;

  // Login scopes: always request openid/profile/email + Graph User.Read
  // If custom API scope exists, also request it
  const loginScopes = ['openid', 'profile', 'email', 'User.Read'];
  if (_apiScope) {
    loginScopes.push(_apiScope);
  }

  _loginRequest = {
    scopes: loginScopes,
    prompt: 'select_account',
  };

  // Token request: custom scope if available, otherwise openid/profile (ID token)
  _apiTokenRequest = {
    scopes: _apiScope ? [_apiScope] : ['openid', 'profile', 'email'],
    forceRefresh: false,
  };

  _msalInstance = new PublicClientApplication(msalConfig);
  await _msalInstance.initialize();

  return _msalInstance;
}

// --- Public accessors (throw if initAuth not called) ---

export function getMsalInstance(): PublicClientApplication {
  if (!_msalInstance) throw new Error('initAuth() not called');
  return _msalInstance;
}

export const get = {
  msalInstance: () => getMsalInstance(),
  loginRequest: (): PopupRequest => {
    if (!_loginRequest) throw new Error('initAuth() not called');
    return _loginRequest;
  },
  apiTokenRequest: () => {
    if (!_apiTokenRequest) throw new Error('initAuth() not called');
    return _apiTokenRequest;
  },
  expectedTenantId: () => {
    if (!_expectedTenantId) throw new Error('initAuth() not called');
    return _expectedTenantId;
  },
  apiScope: () => _apiScope,
};

export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
};
